/**
 * Contains utilities functions
 */

import moment from 'moment';
import { FileType } from '../model';
import * as path from 'path';
import { ApiResponse, ResponseError } from '@sendent/nextcloud-api';
import { logger } from './logger';

/**
 * Formats the string - use {0}, {1} ... for format parameters
 * @param str
 * @param val
 */
const formatString = (str: string, ...val: string[]): string => {
    for (let index = 0; index < val.length; index++) {
        str = str.replace(`{${index}}`, val[index]);
    }
    return str;
};

/**
 * Checks is the object is null or undefined
 * @param obj
 */
const isNANObject = (obj: unknown): obj is null | undefined => {
    return obj === null || obj === undefined;
};

/**
 * Checks is string empty
 * @param value
 */
const isStringEmpty = (value: string | null | undefined): boolean => {
    if (isNANObject(value)) return true;

    return value.length <= 0;
};

/**
 * Emulate sleep
 * @param ms
 */
const sleep = (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Add zero to number if len(number) === 1
 * @param digit
 */
const fillZeroes = (digit: number) => {
    if (digit < 10 && digit >= 0) return '0' + digit;
    else if (digit < 0 && digit > -10) return '-0' + -digit;
    return digit;
};

/**
 * Converts API UTC date
 * @param date
 * @param lang
 */
const formatPlannedDate = (date: string, lang: string) => {
    const m = moment(date, 'YYYY.MM.DD hh:mm:ss');
    const plannedTime = m.toDate();
    const dayName = plannedTime.toLocaleDateString(lang, { weekday: 'long' });
    const planned_date =
        Utilities.fillZeroes(plannedTime.getDate()) +
        '.' +
        Utilities.fillZeroes(plannedTime.getMonth() + 1) +
        '.' +
        plannedTime.getFullYear();
    const delta = plannedTime.getTimezoneOffset();
    plannedTime.setMinutes(plannedTime.getMinutes() - delta);
    const planned_time =
        Utilities.fillZeroes(plannedTime.getHours()) +
        ':' +
        Utilities.fillZeroes(plannedTime.getMinutes());

    return dayName + ', ' + planned_date + ' ' + planned_time;
};

/**
 * Gets local date from API UTC date
 * @param date
 */
const getLocalDate = (date: string): Date => {
    const m = moment(date, 'YYYY.MM.DD hh:mm:ss');
    const plannedTime = m.toDate();
    const delta = plannedTime.getTimezoneOffset();
    plannedTime.setMinutes(plannedTime.getMinutes() - delta);

    return plannedTime;
};

/**
 * Convert date UTC date to local date
 * @param date
 */
const convertToLocalDate = (date: string | number | Date): Date => {
    const dt = new Date();
    const delta = dt.getTimezoneOffset();
    const dateLocal = new Date(date);
    dateLocal.setMinutes(dateLocal.getMinutes() - delta);

    return dateLocal;
};

/**
 * Convert local date to UTC date
 * @param date
 */
const convertToUTCDate = (date: string | number | Date): Date => {
    const dt = new Date();
    const delta = dt.getTimezoneOffset();
    const dateUTC = new Date(date);
    dateUTC.setMinutes(dateUTC.getMinutes() + delta);

    return dateUTC;
};

const parseDatetime = (date_s: string | Date | null | undefined) => {
    if (Utilities.isNANObject(date_s)) return '';
    let date = date_s;
    if (typeof date == 'string') {
        const dateStr = date;
        try {
            const timestamp = Date.parse(dateStr);
            if (isNaN(timestamp) == false) {
                date = new Date(timestamp);
            } else return dateStr;
        } catch (ex) {
            return dateStr;
        }
    }
    return (
        date.getFullYear() +
        '-' +
        ('0' + (date.getMonth() + 1)).slice(-2) +
        '-' +
        ('0' + date.getDate()).slice(-2) +
        ' ' +
        fillZeroes(date.getHours()) +
        ':' +
        fillZeroes(date.getMinutes()) +
        ':' +
        fillZeroes(date.getSeconds())
    );
};

const formatLocaleDate = (date_s: string | Date | null | undefined) => {
    if (Utilities.isNANObject(date_s)) return '';
    let date = date_s;
    if (typeof date == 'string') {
        const dateStr = date;
        try {
            const timestamp = Date.parse(dateStr);
            if (isNaN(timestamp) == false) {
                date = new Date(timestamp);
            } else return dateStr;
        } catch (ex) {
            return dateStr;
        }
    }
    return date.toLocaleDateString();
};

const formatDate = (date: Date | '' | null | undefined): string => {
    logger.debug(`formatDate: ${date}`);
    if (date !== null && date !== undefined && date !== '')
        return (
            date.getFullYear() +
            '-' +
            ('0' + (date.getMonth() + 1)).slice(-2) +
            '-' +
            ('0' + date.getDate()).slice(-2)
        );
    else return '';
};

const formatUploadDate = (date: Date | '' | null | undefined): string => {
    logger.debug(`formatUploadDate: ${date}`);
    if (date !== null && date !== undefined && date !== '')
        return (
            date.getFullYear() +
            '-' +
            fillZeroes(date.getMonth() + 1) +
            '-' +
            fillZeroes(date.getDate())
        );
    else return '';
};

const formatBodyDate = (
    date_s: string | number | Date | null | undefined,
): string => {
    if (date_s !== null && date_s !== undefined && date_s !== '') {
        const date = new Date(date_s);
        return (
            date.getFullYear() +
            '-' +
            ('0' + (date.getMonth() + 1)).slice(-2) +
            '-' +
            ('0' + date.getDate()).slice(-2)
        );
    } else return '';
};

/**
 * Structural view of Document/Element for the XML helpers below. The return
 * type stays `any` on purpose: HTMLCollection can't be spread when typed,
 * with our es5 target (needs downlevelIteration, which would change emit).
 */
type XmlNodeLike = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getElementsByTagName(name: string): any;
};

const getElementNameWithoutNS = (elementNameWithNS: string) => {
    return elementNameWithNS.substring(elementNameWithNS.indexOf(':') + 1);
};

const getElementsByTagName = (node: XmlNodeLike, elementNameWithNS: string) => {
    const elementWithoutNS = getElementNameWithoutNS(elementNameWithNS);
    let retVal = node.getElementsByTagName(elementNameWithNS);
    if (retVal == null || retVal.length == 0) {
        retVal = node.getElementsByTagName(elementWithoutNS);
    }
    return [...retVal];
};

const getElementByTagName = (
    node: XmlNodeLike | null,
    elementNameWithNS: string,
) => {
    if (node === null || node === undefined) return null;

    const retVal = getElementsByTagName(node, elementNameWithNS);
    if (retVal !== null && retVal !== undefined && retVal.length > 0) {
        return retVal[0];
    }
    return null;
};

const getElementTextContent = (node: Element | null): string => {
    return node !== null && node !== undefined ? (node.textContent ?? '') : '';
};

const getElementAttributeId = (node: Element | null) => {
    return node !== null && node !== undefined ? node.getAttribute('Id') : '';
};

const parseDateActivity = (date_s: string | Date | null | undefined) => {
    if (Utilities.isNANObject(date_s)) return '';
    let date = date_s;
    if (typeof date == 'string') {
        const dateStr = date;
        try {
            const timestamp = Date.parse(dateStr);
            if (isNaN(timestamp) == false) {
                date = new Date(timestamp);
            } else return dateStr;
        } catch (ex) {
            return dateStr;
        }
    }
    return (
        date.getFullYear() +
        '-' +
        ('0' + (date.getMonth() + 1)).slice(-2) +
        '-' +
        ('0' + date.getDate()).slice(-2)
    );
};

export const getImageByContetntType = (
    contenttype: string,
    type: FileType | undefined,
    name: string,
) => {
    let img = '';

    if (type === 'email') {
        img = 'assets/email.png';
    } else {
        if (Utilities.isNANObject(contenttype)) {
            if (type === 'file') {
                const ext = path.extname(name).toLowerCase();
                switch (ext) {
                    case '.pdf':
                        img = 'assets/application-pdf.png';
                        break;
                    case '.mp4':
                        img = 'assets/video.png';
                        break;
                    case '.xls':
                    case '.xlsx':
                        img = 'assets/x-office-spreadsheet.png';
                        break;
                    case '.ppt':
                    case '.pptx':
                        img = 'assets/x-office-presentation.png';
                        break;
                    case '.doc':
                    case '.docx':
                        img = 'assets/x-office-document.png';
                        break;
                    case '.txt':
                        img = 'assets/text.png';
                        break;
                    case '.jpg':
                    case '.jpeg':
                        img = 'assets/file.png';
                        break;
                    default:
                        img = 'assets/file.png';
                }
            }
        } else {
            switch (contenttype) {
                case 'application/pdf':
                    img = 'assets/application-pdf.png';
                    break;
                case 'video/mp4':
                    img = 'assets/video.png';
                    break;
                case 'application/vnd.oasis.opendocument.spreadsheet':
                    img = 'assets/x-office-spreadsheet.png';
                    break;
                case 'application/vnd.oasis.opendocument.presentation':
                    img = 'assets/x-office-presentation.png';
                    break;
                case 'application/vnd.oasis.opendocument.text':
                    img = 'assets/x-office-document.png';
                    break;
                case 'application/octet-stream':
                    img = 'assets/text.png';
                    break;
                // eslint-disable-next-line max-len
                case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                    img = 'assets/text.png';
                    break;
                case 'image/jpeg':
                    img = 'assets/file.png';
                    break;
                default:
                    img = 'assets/file.png';
            }
        }
    }

    return img;
};

export const throttle = <T extends (...args: unknown[]) => unknown>(
    func: T,
    ms: number,
): ((...args: Parameters<T>) => void) => {
    let lastCalledAt: number | null = null;

    return function throttled(this: unknown, ...args: Parameters<T>): void {
        const currentTime = Date.now();

        const callTimeDiff = currentTime - Number(lastCalledAt);
        if (callTimeDiff < ms) {
            return;
        }

        lastCalledAt = currentTime;
        func.apply(this, args);
    };
};

export const capitilize = (str: string): string =>
    str.charAt(0).toUpperCase() + str.slice(1);

type FormatValues = {
    [key: string]: string;
};

export const replaceAllInString = (str: string, formatValues: FormatValues) =>
    Object.entries(formatValues).reduce(
        (str, [key, value]) => str.replaceAll(key, value),
        str,
    );

export const countBeforeIndex = <T>(
    arr: T[],
    stopIndex: number,
    condition: (item: T, index: number, arr: T[]) => boolean,
): number => {
    let counter = 0;
    for (let i = 0; i < stopIndex; i++) {
        if (!condition(arr[i], i, arr)) {
            continue;
        }
        counter++;
    }
    return counter;
};

export const isResponseError = (
    res: ApiResponse | ResponseError,
): res is ResponseError => !res.success;

export const mapInChunks = async <T, R>(
    items: T[],
    chunkSize: number,
    worker: (item: T, index: number) => Promise<R>,
): Promise<R[]> => {
    const results: R[] = [];
    for (let i = 0; i < items.length; i += chunkSize) {
        const batch = items.slice(i, i + chunkSize);
        const chunkResults = await Promise.all(
            batch.map((item, batchIndex) => worker(item, i + batchIndex)),
        );
        results.push(...chunkResults);
    }
    return results;
};

export const Utilities = {
    formatString,
    isNANObject,
    isStringEmpty,
    sleep,
    fillZeroes,
    formatPlannedDate,
    getLocalDate,
    convertToLocalDate,
    convertToUTCDate,
    parseDatetime,
    formatDate,
    formatUploadDate,
    formatBodyDate,
    getElementsByTagName,
    getElementByTagName,
    getElementTextContent,
    parseDateActivity,
    formatLocaleDate,
    getElementAttributeId,
};
